import { useQuery } from '@apollo/client';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types.ts';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable.tsx';
import { Table, TableName, type TableProps } from '@openx/components-table/Table.tsx';
import type { SelectOperation } from '@openx/types/selectableTable.ts';
import type { AudienceStatus } from '@openx/types/targeting/audience.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { GetAudienceAccountsDocument } from '../../../graphql/query/audiences/GetAudienceAccounts.generated.tsx';
import type { GetAudiencesListQuery } from '../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { GetProvidersDocument } from '../../../graphql/query/audiences/GetProviders.generated.tsx';
import { GetUsersListDocument } from '../../../graphql/query/users/GetUsersList.generated.tsx';
import { isAdmin, isInternalUser } from '../../../permissions/validators.ts';
import { UNSUPPORTED_STATUSES_FOR_DEAL } from '../constants.ts';
import { AudienceListTopBar } from './components/AudienceListTopBar.tsx';
import { useAudienceListFetch } from './hooks/useAudienceListFetch.tsx';
import { useAudienceStatuses } from './hooks/useAudienceStatusOptions.ts';
import { useRowActions } from './hooks/useRowActions.tsx';
import { useTableColumns } from './hooks/useTableColumns.tsx';
import type { AudiencesListFilters } from './types.ts';
import { getFiltersConfig } from './utils/filtersConfig.ts';

const AudiencesSelectableTable = SelectableTable<
  GetAudiencesListQuery['audience'][number],
  AudiencesListFilters,
  TableProps<GetAudiencesListQuery['audience'][number], AudiencesListFilters>
>(Table);

type AudienceListProps = {
  selectedAudiences: GetAudiencesListQuery['audience'];
  setSelectedAudiences: Dispatch<SetStateAction<GetAudiencesListQuery['audience']>>;
};

export const AudienceList = ({ selectedAudiences, setSelectedAudiences }: AudienceListProps) => {
  const {
    criteria,
    audiences,
    handleCriteriaChange,
    loading: loadingListData,
    refetch,
    handleFiltersChange,
    error: audienceError,
  } = useAudienceListFetch();
  const { session } = useAuth();
  const { t } = useTranslation();
  const columns = useTableColumns(criteria.filters?.phrase);
  const rowActions = useRowActions(refetch, setSelectedAudiences);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: { account: audienceAccounts = [] } = {},
    loading: loadingAccounts,
    error: accountError,
  } = useQuery(GetAudienceAccountsDocument);
  const { data: { provider = [] } = {}, loading: loadingProviders } = useQuery(GetProvidersDocument);
  const { loadingAudienceStatuses, options: audienceStatusOptions } = useAudienceStatuses();

  const isInternalOxUser = isInternalUser(session);
  const isInternalAdmin = isInternalOxUser && isAdmin(session);
  const criteriaAccounts = Array.isArray(criteria.filters?.account) ? criteria.filters.account : [];

  const criteriaAccountIds = audienceAccounts
    .filter(account => criteriaAccounts.some(accountName => accountName === account.name))
    .map(account => account.id);

  const organizationIds = !isInternalAdmin
    ? [session?.account_id, ...criteriaAccountIds].filter(val => !!val)
    : undefined;

  const {
    loading: loadingUsers,
    data: { user: users = [] } = {},
    error: usersError,
  } = useQuery(GetUsersListDocument, {
    variables: {
      organizationIds: organizationIds,
    },
  });

  const redirectToDealCreation = useCallback(
    (audience: GetAudiencesListQuery['audience'][number]) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('fallback', 'true');

      navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
      navigate('/deals/create', {
        state: {
          audienceId: audience.id,
          accountUid: audience.account?.uid,
          useAsTemplate: true,
        },
      });
    },
    [location.pathname, location.search, navigate]
  );

  const handleSelectionChange = useCallback(
    (selectedItems: GetAudiencesListQuery['audience'], operation: SelectOperation) => {
      setSelectedAudiences(
        applySelectionChange({
          previouslySelectedItems: selectedAudiences,
          changeItems: selectedItems,
          operation,
          itemsComparer: (a, b) => a.id === b.id,
        })
      );
    },
    [selectedAudiences, setSelectedAudiences]
  );

  const adjustedColumns = useMemo(
    () => (isInternalOxUser ? columns : columns.filter(({ key }) => key !== 'account')),
    [isInternalOxUser, columns]
  );

  const providerOptions = useMemo(
    () =>
      provider.map(provider => ({
        name: provider.name,
        id: provider.id,
      })),
    [provider]
  );

  const userOptions = useMemo(
    () =>
      users.map(user => ({
        name: `${user.first_name} ${user.last_name} (${user.email_address})`,
        id: user.id,
      })),
    [users]
  );

  const accountOptions = useMemo(
    () =>
      audienceAccounts.map(account => ({
        name: account.name,
        id: account.name,
      })),
    [audienceAccounts]
  );

  const filtersProviderConfig: FiltersContextProviderProps<AudiencesListFilters>['config'] = {
    filtersConfig: getFiltersConfig({
      userId: session?.id,
      t,
      isInternalOxUser,
      providerOptions,
      userOptions,
      accountOptions,
      audienceStatusOptions,
    }),
    visibleFilters: ['my_audiences', 'addressable_reach', 'account', 'direct_audience_provider'],
    criteria,
    disabledFilters: [],
    onFiltersChange: handleFiltersChange,
  };

  const loading = loadingListData || loadingProviders || loadingUsers || loadingAccounts || loadingAudienceStatuses;
  const error = audienceError ?? (accountError || usersError);

  if (error) throw new Error('Audiences Error');

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <AudienceListTopBar />
      <AudiencesSelectableTable
        toggleSelect={handleSelectionChange}
        selectedItems={selectedAudiences}
        columns={adjustedColumns}
        criteria={criteria}
        data={audiences}
        loading={loading}
        name={TableName.AUDIENCES}
        onCriteriaChange={handleCriteriaChange}
        rowActions={rowActions}
        primaryRowAction={{
          label: t('Create Deal'),
          onClick: redirectToDealCreation,
          allowed: audience =>
            !(UNSUPPORTED_STATUSES_FOR_DEAL as readonly string[]).includes(audience.status as AudienceStatus),
          notAllowedMessage: t('Deals cannot be created using an expired or failed audience.'),
        }}
      />
    </FiltersContextProvider>
  );
};
